import { config } from "@neru/base/config";
import { useAuth, useNotifications, useUserAccounts } from "@neru/base/hooks";
import {
  AuthServiceProvider,
  CookieSessionProvider,
} from "@neru/base/services";
import { type AccountIdProp } from "@neru/base/types";
import { getLogger, mapApiRoute } from "@neru/base/utils";
import type { WithChildren } from "@neru/types";
import { Spinner } from "@neru/ui";
import { useEffect } from "react";

const log = getLogger("IdentityAwareProxy");

const authService = new CookieSessionProvider(
  mapApiRoute(config.graphqlApiUrl),
);

const WithUserAccounts = ({
  accountId,
  children,
}: WithChildren & AccountIdProp) => {
  const { showError } = useNotifications();
  const { saveAccountId } = useAuth();

  const { error, loading } = useUserAccounts();

  if (error) {
    const message = typeof error === "string" ? error : error.message;
    showError(`Failed to fetch accounts: ${message}`);
  }

  useEffect(() => {
    if (new AuthServiceProvider(authService)) {
      log.info("Auth service provider is initialized.");
    }
  }, []);

  useEffect(() => {
    saveAccountId(accountId);
  }, [accountId, saveAccountId]);

  if (loading) {
    return <Spinner data-testid="user-accounts" />;
  }

  return children;
};

const WithAuth = ({ accountId, children }: WithChildren & AccountIdProp) => (
  <WithUserAccounts accountId={accountId}>{children}</WithUserAccounts>
);

export default WithAuth;
